import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import superAdminApiClient from '../../../api/superAdminApiClient';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { Table, Form, Row, Col, Pagination, InputGroup, Card, Badge, Button } from 'react-bootstrap';
import { formatDateOnly } from '../../../utils/dateUtils';
import usePermission from '../../../hooks/usePermission';

const MODULE_LABELS = {
    holidays: 'Holidays',
    hotels: 'Hotels',
    cabs: 'Cabs',
    yatras: 'Tirth Yatra',
    medical_tourism: 'Medical Tourism'
};

const MODULE_BADGES = {
    holidays: 'bg-primary-subtle text-primary',
    hotels: 'bg-success-subtle text-success',
    cabs: 'bg-warning-subtle text-warning',
    yatras: 'bg-info-subtle text-info',
    medical_tourism: 'bg-danger-subtle text-danger'
};

const STATUS_BADGES = {
    active: 'bg-success',
    inactive: 'bg-danger',
    draft: 'bg-secondary'
};

const CustomSeoUrlListing = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { hasPermission } = usePermission();
    const [seoUrls, setSeoUrls] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filters and Pagination State
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedModule, setSelectedModule] = useState('all');
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const itemsPerPage = 10;

    useEffect(() => {
        if (location.state?.successMessage) {
            toast.success(location.state.successMessage, { toastId: 'seo-url-success-toast' });
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, location.pathname, navigate]);

    const fetchSeoUrls = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page: currentPage,
                limit: itemsPerPage
            };
            if (searchTerm.trim()) params.search = searchTerm.trim();
            if (selectedModule !== 'all') params.module = selectedModule;
            if (selectedStatus !== 'all') params.status = selectedStatus;

            const response = await superAdminApiClient.get('/superadmin/custom-seo-urls', { params });
            if (response.data.success) {
                setSeoUrls(response.data.data || []);
                if (response.data.pagination) {
                    setTotalPages(response.data.pagination.totalPages || 1);
                    setTotalRecords(response.data.pagination.total || 0);
                }
            }
        } catch (error) {
            console.error('Error fetching custom SEO URLs:', error);
            toast.error('Failed to fetch Custom SEO URLs');
        } finally {
            setLoading(false);
        }
    }, [currentPage, searchTerm, selectedModule, selectedStatus]);

    useEffect(() => {
        fetchSeoUrls();
    }, [fetchSeoUrls]);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const handleModuleChange = (e) => {
        setSelectedModule(e.target.value);
        setCurrentPage(1);
    };

    const handleStatusChange = (e) => {
        setSelectedStatus(e.target.value);
        setCurrentPage(1);
    };

    const handleStatusToggle = async (id, currentStatus) => {
        const nextStatus = currentStatus === 'active' ? 'inactive' : 'active';
        try {
            const response = await superAdminApiClient.patch(`/superadmin/custom-seo-urls/${id}/status`, { status: nextStatus });
            if (response.data.success) {
                toast.success(`URL set to ${nextStatus}`);
                fetchSeoUrls();
            }
        } catch (error) {
            console.error('Status update failed:', error);
            toast.error('Failed to update status');
        }
    };

    const handleDelete = async (id, customUrl) => {
        Swal.fire({
            title: 'Delete Custom SEO URL?',
            html: `Are you sure you want to delete <strong>${customUrl}</strong>? This action cannot be undone.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await superAdminApiClient.delete(`/superadmin/custom-seo-urls/${id}`);
                    if (response.data.success) {
                        Swal.fire('Deleted!', 'Custom SEO URL deleted successfully.', 'success');
                        fetchSeoUrls();
                    }
                } catch (error) {
                    toast.error('Failed to delete Custom SEO URL');
                }
            }
        });
    };

    const formatLinkedData = (item) => {
        const qParams = item.query_params || {};
        if (item.linked_type === 'existing_page' && (qParams.target_url || qParams.existing_page_url)) {
            return `Existing Page: ${qParams.target_url || qParams.existing_page_url}`;
        }
        const parts = [];
        if (qParams.city) parts.push(`City: ${qParams.city}`);
        if (qParams.searchQuery) parts.push(`Search: "${qParams.searchQuery}"`);
        if (qParams.from_city && qParams.to_city) parts.push(`Route: ${qParams.from_city} → ${qParams.to_city}`);
        if (qParams.category) parts.push(`Category: ${qParams.category}`);
        if (item.linked_record_id) parts.push(`ID #${item.linked_record_id}`);

        return parts.length > 0 ? parts.join(' | ') : 'All Module Listings';
    };

    return (
        <div className="page-content">
            <div className="container-fluid">
                {/* Header Title */}
                <div className="row mb-3">
                    <div className="col-12 d-flex align-items-center justify-content-between flex-wrap gap-2">
                        <div>
                            <h4 className="mb-1 text-dark fw-bold">SEO Management</h4>
                            <p className="text-muted mb-0">Create & manage custom SEO landing URLs with metadata and content</p>
                        </div>
                        {hasPermission('seo.add') && (
                            <Link to="/superadmin/seo/custom-urls/add" className="btn btn-primary d-inline-flex align-items-center gap-1">
                                <i className="ri-add-line fs-18"></i> Create Custom SEO URL
                            </Link>
                        )}
                    </div>
                </div>

                {/* Filters */}
                <Card className="border shadow-sm mb-4">
                    <Card.Body className="p-3">
                        <Row className="g-3 align-items-center">
                            <Col md={5} lg={5}>
                                <InputGroup>
                                    <InputGroup.Text className="bg-light">
                                        <i className="ri-search-line"></i>
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="text"
                                        placeholder="Search Custom URL or Meta Title..."
                                        value={searchTerm}
                                        onChange={handleSearchChange}
                                    />
                                    {searchTerm && (
                                        <Button variant="outline-secondary" onClick={() => { setSearchTerm(''); setCurrentPage(1); }}>
                                            Clear
                                        </Button>
                                    )}
                                </InputGroup>
                            </Col>

                            <Col md={3} lg={3}>
                                <Form.Select value={selectedModule} onChange={handleModuleChange}>
                                    <option value="all">All Modules</option>
                                    <option value="holidays">Holidays</option>
                                    <option value="hotels">Hotels</option>
                                    <option value="cabs">Cabs</option>
                                    <option value="yatras">Tirth Yatra</option>
                                    <option value="medical_tourism">Medical Tourism</option>
                                </Form.Select>
                            </Col>

                            <Col md={3} lg={3}>
                                <Form.Select value={selectedStatus} onChange={handleStatusChange}>
                                    <option value="all">All Statuses</option>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                    <option value="draft">Draft</option>
                                </Form.Select>
                            </Col>

                            <Col md={1} lg={1} className="text-end">
                                <span className="badge bg-light text-dark border p-2">
                                    Total: {totalRecords}
                                </span>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* Table */}
                <Card className="border shadow-sm">
                    <Card.Body className="p-0">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status"></div>
                                <p className="mt-2 text-muted mb-0">Loading Custom SEO URLs...</p>
                            </div>
                        ) : seoUrls.length === 0 ? (
                            <div className="text-center py-5">
                                <i className="ri-link-unlink-m fs-48 text-muted"></i>
                                <h5 className="mt-2">No Custom SEO URLs Found</h5>
                                <p className="text-muted mb-3">No matching records found. Create one to get started.</p>
                                {hasPermission('seo.add') && (
                                    <Link to="/superadmin/seo/custom-urls/add" className="btn btn-sm btn-primary">
                                        <i className="ri-add-line me-1"></i> Add Custom SEO URL
                                    </Link>
                                )}
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <Table hover align="middle" className="mb-0 border-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Custom URL</th>
                                            <th>Module</th>
                                            <th>Linked Page / Data</th>
                                            <th>Meta Title</th>
                                            <th>Status</th>
                                            <th>Created Date</th>
                                            <th>Updated Date</th>
                                            <th className="text-end pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {seoUrls.map((item) => (
                                            <tr key={item.id}>
                                                <td>
                                                    <span className="fw-semibold text-primary">{item.custom_url}</span>
                                                    <div className="text-muted small">
                                                        Position: <span className="text-capitalize">{item.content_position} listings</span>
                                                    </div>
                                                </td>

                                                <td>
                                                    <Badge className={`${MODULE_BADGES[item.module] || 'bg-secondary'} border px-2 py-1`}>
                                                        {MODULE_LABELS[item.module] || item.module}
                                                    </Badge>
                                                </td>

                                                <td>
                                                    <span className="text-dark small fw-medium">{formatLinkedData(item)}</span>
                                                </td>

                                                <td style={{ maxWidth: '220px' }}>
                                                    <div className="text-truncate" title={item.meta_title}>
                                                        {item.meta_title}
                                                    </div>
                                                </td>

                                                <td>
                                                    <Badge className={`${STATUS_BADGES[item.status] || 'bg-secondary'} text-capitalize`}>
                                                        {item.status}
                                                    </Badge>
                                                </td>

                                                <td className="small text-muted">
                                                    {formatDateOnly(item.created_at)}
                                                </td>

                                                <td className="small text-muted">
                                                    {formatDateOnly(item.updated_at)}
                                                </td>

                                                <td className="text-end pe-4">
                                                    <div className="d-flex align-items-center justify-content-end gap-1">
                                                        {/* Preview / View */}
                                                        <a
                                                            href={`${item.custom_url}?preview=true`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="btn btn-sm btn-outline-info"
                                                            title="Preview Landing Page"
                                                        >
                                                            <i className="ri-eye-line"></i>
                                                        </a>

                                                        {/* Edit */}
                                                        {hasPermission('seo.edit') && (
                                                            <Link
                                                                to={`/superadmin/seo/custom-urls/edit/${item.id}`}
                                                                className="btn btn-sm btn-outline-primary"
                                                                title="Edit Configuration"
                                                            >
                                                                <i className="ri-pencil-line"></i>
                                                            </Link>
                                                        )}

                                                        {/* Status Toggle */}
                                                        {hasPermission('seo.edit') && (
                                                            <button
                                                                type="button"
                                                                className={`btn btn-sm ${item.status === 'active' ? 'btn-outline-warning' : 'btn-outline-success'}`}
                                                                title={item.status === 'active' ? 'Deactivate' : 'Activate'}
                                                                onClick={() => handleStatusToggle(item.id, item.status)}
                                                            >
                                                                <i className={item.status === 'active' ? 'ri-close-circle-line' : 'ri-checkbox-circle-line'}></i>
                                                            </button>
                                                        )}

                                                        {/* Delete */}
                                                        {hasPermission('seo.delete') && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-danger"
                                                                title="Delete"
                                                                onClick={() => handleDelete(item.id, item.custom_url)}
                                                            >
                                                                <i className="ri-delete-bin-line"></i>
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </Card.Body>

                    {/* Footer Pagination */}
                    {totalPages > 1 && (
                        <Card.Footer className="bg-white d-flex align-items-center justify-content-between py-2">
                            <span className="text-muted small">
                                Page {currentPage} of {totalPages} ({totalRecords} items)
                            </span>
                            <Pagination className="mb-0 pagination-sm">
                                <Pagination.Prev
                                    disabled={currentPage === 1}
                                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                />
                                {[...Array(totalPages)].map((_, idx) => {
                                    const pageNum = idx + 1;
                                    return (
                                        <Pagination.Item
                                            key={pageNum}
                                            active={pageNum === currentPage}
                                            onClick={() => setCurrentPage(pageNum)}
                                        >
                                            {pageNum}
                                        </Pagination.Item>
                                    );
                                })}
                                <Pagination.Next
                                    disabled={currentPage === totalPages}
                                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                />
                            </Pagination>
                        </Card.Footer>
                    )}
                </Card>
            </div>
        </div>
    );
};

export default CustomSeoUrlListing;
